import { useMemo } from 'react';
import { useStanQuery } from '.';
import { QUERY_KEYS } from '@/lib';
import { useAuthStore } from '@/stores';
import { getExperimentCommunityStats, getExperimentMetadata, getExperimentsCommunities } from '@/lib/api/experiments';

const EXPERIMENT_NAME = 'gary_vee_challenge_experiment';

interface ChallengeData {
    communityMagicLink?: string;
    challengeKeyDate?: string;
    challengeText: string;
    communityStats: {
        total_members?: number;
        total_products_made?: number;
        total_products_published?: number;
        total_revenue?: number;
        gv_total_revenue?: number;
    } | null;
    initialized: boolean;
}

/**
 * Fetches challenge metadata by combining three API calls in parallel
 * Similar to the Vuex store's fetchChallengeMetaDataInternal function
 */
async function fetchChallengeData(): Promise<ChallengeData> {
    try {
        const [communitiesResponse, metadataResponse, statsResponse] = await Promise.all([
            getExperimentsCommunities(),
            getExperimentMetadata(),
            getExperimentCommunityStats(),
        ]);

        const challengeData: ChallengeData = {
            communityMagicLink: undefined,
            challengeKeyDate: undefined,
            challengeText: 'CHALLENGE ENDS',
            communityStats: null,
            initialized: true,
        };

        // Extract data from communities response
        if (communitiesResponse[EXPERIMENT_NAME]?.magic_link) {
            challengeData.communityMagicLink = communitiesResponse[EXPERIMENT_NAME].magic_link;
        }

        // Extract data from metadata response
        if (metadataResponse[EXPERIMENT_NAME]?.key_date) {
            challengeData.challengeKeyDate = metadataResponse[EXPERIMENT_NAME].key_date;
        }

        if (metadataResponse[EXPERIMENT_NAME]?.key_text) {
            challengeData.challengeText = metadataResponse[EXPERIMENT_NAME].key_text;
        }

        // Extract data from stats response
        if (statsResponse[EXPERIMENT_NAME]) {
            challengeData.communityStats = statsResponse[EXPERIMENT_NAME];
        }

        return challengeData;
    } catch (error) {
        console.error('[CHALLENGE] Error fetching challenge data:', error);
        // Return default values on error
        return {
            communityMagicLink: undefined,
            challengeKeyDate: undefined,
            challengeText: 'CHALLENGE ENDS',
            communityStats: null,
            initialized: true,
        };
    }
}

/**
 * Hook to fetch and access challenge-related data
 * Replaces the Vuex Challenge store
 */
export function useChallenge() {
    const token = useAuthStore(state => state.token);
    const enabled = useAuthStore(state => state.isAuthenticated);

    const {
        data: challengeData,
        isLoading,
        error,
        refetch,
    } = useStanQuery({
        queryKey: [QUERY_KEYS.EXPERIMENTS, 'challenge', token],
        queryFn: fetchChallengeData,
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled,
        retry: false,
        meta: {
            error: {
                sentryMessage: '[CHALLENGE] failed-to-fetch-challenge-data',
                title: 'Unable to load challenge data',
                message: 'Unable to load challenge data. Please try again.',
            },
        },
    });

    // Memoize the return values to match Vuex getters pattern
    const communityMagicLink = useMemo(() => challengeData?.communityMagicLink, [challengeData]);
    const challengeKeyDate = useMemo(() => challengeData?.challengeKeyDate, [challengeData]);
    const challengeText = useMemo(() => challengeData?.challengeText || 'CHALLENGE ENDS', [challengeData]);
    const communityStats = useMemo(() => challengeData?.communityStats || null, [challengeData]);
    const initialized = useMemo(() => challengeData?.initialized ?? false, [challengeData]);

    return {
        // Getters (matching Vuex getters)
        communityMagicLink,
        challengeKeyDate,
        challengeText,
        communityStats,
        initialized,
        // Additional React Query properties
        isLoading,
        error,
        refetch,
    };
}
